import { useState } from "react";
import { Clock12 } from "lucide-react";
import { MomentShell, SaveButton } from "../day/MomentShell";
import { Chips, Field, inputCls } from "../day/Chips";
import { isCompleted, stepText, modeLabel, hhmm } from "./labels";

function Adjustments({ rec, onAction }) {
  const [open, setOpen] = useState(null);
  const [custom, setCustom] = useState("");
  const [time, setTime] = useState("");
  const [obstacle, setObstacle] = useState(rec.focus?.obstacle || "");
  const [saving, setSaving] = useState(false);
  const run = async (payload) => { setSaving(true); const ok = await onAction(payload); setSaving(false); if (ok) setOpen(null); };
  const Tab = ({ id, label }) => (
    <button type="button" onClick={() => setOpen(open === id ? null : id)} data-testid={`c2g-adjust-${id}`}
      className={`rounded-xl border px-2 py-2.5 text-sm font-semibold ${open === id ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F4F4F5]" : "border-[#27272A] bg-[#0D0D0F] text-[#B4B4BC]"}`}>{label}</button>
  );
  return (
    <Field label="Vuoi adattare il passo? (facoltativo)">
      <div className="mt-2 grid grid-cols-3 gap-2"><Tab id="reduce" label="Riduci" /><Tab id="reschedule" label="Sposta" /><Tab id="obstacle" label="Ostacolo" /></div>
      {open === "reduce" && (
        <div className="mt-3 space-y-2" data-testid="c2g-reduce-form">
          <button type="button" disabled={saving} onClick={() => run({ action: "reduce", mode: "minimum" })} data-testid="c2g-reduce-minimum" className="w-full rounded-xl border border-[#27272A] bg-[#0D0D0F] p-3 text-left text-sm text-[#F4F4F5]">Usa la forma breve: {rec.selectedStep.minimumText}</button>
          <input value={custom} onChange={(e) => setCustom(e.target.value)} maxLength={300} placeholder="Oppure scrivi tu una riduzione del passo" className={inputCls} data-testid="c2g-reduce-custom-input" />
          <SaveButton onClick={() => run({ action: "reduce", mode: "custom", custom_text: custom.trim() })} disabled={!custom.trim()} saving={saving} label="Usa questa riduzione" testid="c2g-reduce-custom-save" />
        </div>
      )}
      {open === "reschedule" && (
        <div className="mt-3" data-testid="c2g-reschedule-form">
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} data-testid="c2g-reschedule-time" />
          <SaveButton onClick={() => run({ action: "reschedule", time })} disabled={!time} saving={saving} label="Sposta a quest'ora" testid="c2g-reschedule-save" />
        </div>
      )}
      {open === "obstacle" && (
        <div className="mt-3" data-testid="c2g-obstacle-form">
          <input value={obstacle} onChange={(e) => setObstacle(e.target.value)} maxLength={300} placeholder="Che cosa ti sta ostacolando?" className={inputCls} data-testid="c2g-obstacle-input" />
          <SaveButton onClick={() => run({ action: "obstacle", obstacle: obstacle.trim() })} disabled={!obstacle.trim()} saving={saving} label="Registra l'ostacolo" testid="c2g-obstacle-save" />
        </div>
      )}
    </Field>
  );
}

function Recorded({ rec, tz }) {
  const f = rec.focus || {};
  if (!f.reducedText && !f.rescheduledAt && !f.obstacle) return null;
  return (
    <div className="mt-3 space-y-1 rounded-xl border border-[#27272A] bg-[#0D0D0F] p-3.5 text-sm text-[#B4B4BC]" data-testid="c2g-focus-recorded">
      {f.reducedText && <p>Riduzione registrata: <span className="text-[#F4F4F5]">{f.reducedText}</span></p>}
      {f.rescheduledAt && <p>Spostato alle <span className="tabular-nums text-[#F4F4F5]">{hhmm(f.rescheduledAt, tz)}</span></p>}
      {f.obstacle && <p>Ostacolo: <span className="text-[#F4F4F5]">{f.obstacle}</span></p>}
    </div>
  );
}

export function FocusPanel({ f, rec, editable, time, tz, onAction }) {
  const selected = !!rec?.selectedStep?.choiceId;
  const done = selected && isCompleted(rec.status);
  const closed = !!rec?.evening?.closed;
  return (
    <MomentShell icon={Clock12} label="Mezzogiorno (facoltativo)" time={time} title={f.title} text={selected && !done && !closed ? f.body : null} done={!!rec?.focus?.answered} index={1} testid="c2g-focus">
      {!selected ? (
        <p className="a2g-read mt-2 text-base text-[#8A8E96]" data-testid="c2g-focus-no-step">Il Mezzogiorno si attiva dopo la scelta del passo al Mattino.</p>
      ) : (
        <>
          <div className="mt-3 rounded-xl border border-[#27272A] bg-[#0D0D0F] p-3.5" data-testid="c2g-focus-step">
            <p className="text-[12px] font-semibold uppercase tracking-wider text-[#D4AF37]">Il tuo passo{modeLabel(rec) ? ` · ${modeLabel(rec)}` : ""}</p>
            <p className="a2g-read mt-1 text-base text-[#F4F4F5]">{stepText(rec)}</p>
          </div>
          {done ? (
            <p className="a2g-read mt-3 text-base text-[#10B981]" data-testid="c2g-focus-completed">Hai confermato il passo. Alla Sera ti basterà chiudere la giornata.</p>
          ) : editable ? (
            <>
              <Field label={f.status_question}>
                <Chips options={f.status_options.map((o) => ({ value: o.id, label: o.label }))} value={rec.focus?.status} onChange={(v) => v && onAction({ action: "focus", status: v })} testid="c2g-focus-status" />
              </Field>
              <Recorded rec={rec} tz={tz} />
              <Adjustments key={rec.updatedAt} rec={rec} onAction={onAction} />
            </>
          ) : <Recorded rec={rec} tz={tz} />}
        </>
      )}
    </MomentShell>
  );
}
